import { FormEvent, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { AuthGetTokens } from "@/legacy/ApiAuth";
import { ApiAgentInviteRedeem, AuthGetLoggedinAgent } from "@/legacy/ApiCallerOld";
import { authUpdated } from "../../Store/Reducers/authSlice";
import { useAppDispatch } from "../../Store/hooks";
import { agentLoggedIn } from "../../Store/Reducers/loggedInAgentSlice";
import { AlertBox } from "@shared/Components/AlertBox/AlertBox";
import { AlertType } from "@shared/Components/AlertBox/AlertType";
import { AgentInviteRedeem } from "@shared/Models/Requests/AgentInvite";

export function AgentRegister() {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const [searchParams] = useSearchParams();

	const email = searchParams.get("email");
	const code = searchParams.get("code");

	const validUrlParams = email != null && code != null;

	const [reqInProgress, setReqInProgress] = useState(false);
	const [alertType, setAlertType] = useState<AlertType | undefined>();
	const [alertText, setAlertText] = useState("");

	/** Attempts to create the user and then login. */
	async function handleSubmit(event: FormEvent) {
		event.preventDefault();

		const name = (document.getElementById("nameInput") as HTMLInputElement)?.value ?? "";
		const password = (document.getElementById("passwordInput") as HTMLInputElement)?.value ?? "";
		const passwordConfirm = (document.getElementById("confirmPasswordInput") as HTMLInputElement)?.value ?? "";

		// Check passwords match.
		if (password != passwordConfirm) {
			setReqInProgress(false);
			setAlertType("error");
			setAlertText("Password' and confirm password fields must match.");

			return;
		}

		if (email == undefined) {
			setReqInProgress(false);
			setAlertType("error");
			setAlertText("Email not found.");

			return;
		}

		if (code == undefined) {
			setReqInProgress(false);
			setAlertType("error");
			setAlertText("Code not found.");

			return;
		}

		setReqInProgress(true);
		setAlertType(undefined);
		setAlertText("");

		// Create account.
		const toastId1 = toast.loading("Creating account...");

		const data: AgentInviteRedeem = {
			code: code,
			name: name,
			password: password
		};

		const createResponse = await ApiAgentInviteRedeem(data);

		if (createResponse.successful && createResponse.data != null) {
			if (createResponse.data[0] == "SUCCESS") {
				toast.success("Account created.", { id: toastId1 });
			} else {
				setReqInProgress(false);
				setAlertType("error");
				setAlertText(createResponse.data[0]);

				return;
			}
		} else {
			setReqInProgress(false);
			toast.error("Could not create account. " + createResponse.errorCode + ": " + createResponse.errorMsg, { id: toastId1 });
			return;
		}

		// Login

		const tokenRes = await AuthGetTokens(email, password);

		if (!tokenRes.successful) {
			setAlertType("error");
			setAlertText("Error in auth: " + tokenRes.errorMsg);
		}

		setReqInProgress(false);

		if (tokenRes.data != null) {
			dispatch(authUpdated({ accessToken: tokenRes.data.accessToken, refreshToken: tokenRes.data.refreshToken }));

			const loggedInAgentRes = await AuthGetLoggedinAgent();

			if (loggedInAgentRes.successful) {
				if (loggedInAgentRes.data?.id != null) {
					dispatch(agentLoggedIn(loggedInAgentRes.data));
					navigate("/home");
				} else {
					setAlertType("error");
					setAlertText("Error in auth: Could not log you in.");
				}
			} else {
				setAlertType("error");
				setAlertText("Error in auth: " + loggedInAgentRes.errorMsg);
			}
		} else if (tokenRes.errorCode == 401) {
			setAlertType("warning");
			setAlertText("Incorrect Credentials.");
		} else {
			setAlertType("error");
			setAlertText("Unknown auth error.");
		}
	}

	if (!validUrlParams) {
		return <BadLinkErrorPage />;
	}

	return (
		<div className="h-screen flex flex-col" style={{ backgroundColor: "#E1F2FE" }}>
			<div className="grow pt-10 sm:pt-16 px-5">
				<div className="max-w-md w-full mx-auto bg-gray-50 py-8 px-5 rounded-md shadow-md">
					<div className="text-xl mb-6">
						Create your GoDesk agent account
					</div>

					{alertType != null &&
						<AlertBox alertType={alertType}>
							{alertText}
						</AlertBox>}

					<form className="" onSubmit={handleSubmit}>
						<label htmlFor="nameInput">Name</label>
						<input
							id="nameInput"
							type="text"
							placeholder="John Smith"
							disabled={reqInProgress}
							required
							className="mt-1 block w-full shadow-sm border-gray-300 rounded-md py-2 px-3 border focus:border-blue-500 mb-4"
						/>

						<label htmlFor="emailInput">Email address</label>
						<input
							id="emailInput"
							defaultValue={email ?? ""}
							type="text"
							placeholder="me@company.com"
							disabled={reqInProgress}
							required
							readOnly
							autoComplete="username"
							className="mt-1 block w-full shadow-sm border-gray-300 rounded-md py-2 px-3 border focus:border-blue-500 mb-4 text-gray-700 cursor-pointer"
						/>

						<label htmlFor="passwordInput">Password</label>
						<input
							id="passwordInput"
							type="password"
							placeholder="&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;"
							disabled={reqInProgress}
							required
							autoComplete="new-password"
							className="mt-1 block w-full shadow-sm border-gray-300 rounded-md py-2 px-3 border focus:border-blue-500 mb-4"
						/>

						<label htmlFor="confirmPasswordInput">Confirm password</label>
						<input
							id="confirmPasswordInput"
							type="password"
							placeholder="&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;"
							disabled={reqInProgress}
							required
							autoComplete="new-password"
							className="mt-1 block w-full shadow-sm border-gray-300 rounded-md py-2 px-3 border focus:border-blue-500"
						/>

						<div className="w-full flex flex-row justify-center">
							<input className="btn-blue mt-4 cursor-pointer" type="submit" value="Register" disabled={reqInProgress} />
						</div>
					</form>
				</div>
			</div>
		</div>
	);
}

function BadLinkErrorPage() {
	return (
		<div className="h-screen flex flex-col" style={{ backgroundColor: "#E1F2FE" }}>
			<div className="grow pt-10 sm:pt-16 px-5">
				<div className="max-w-lg w-full mx-auto bg-gray-50 py-8 px-5 rounded-md shadow-md">
					<AlertBox alertType="error">
						Invalid redeem link. Did you open an old invite email?
					</AlertBox>

					Contact your GoDesk admin to obtain a new invite link.
				</div>
			</div>
		</div>
	);
}
